// @flow
import React, { Fragment, useState, } from 'react';
import { useFela, } from 'react-fela';
import { ApolloConsumer, TabButton, TabList, Tab, } from '@haaretz/htz-components';

import type { Node, } from 'react';
import type { Filter, } from '@haaretz/htz-components';

import Tabs from '../Tabs/Tabs';
import TabPanel from '../TabPanel/TabPanel';
import BondsTabsQuery from '../../pages/queries/bondsTabs.graphql';

type TabType = {
  display: string,
  controls: string,
  filters: Filter[],
  headers?: string[],
};

type Props = {
  defaultTab: number,
  panel: ({ filters: Filter[], headers: ?(string[]), }) => Node,
  tabs: Array<TabType>,
  presentation: boolean,
};

const incByKey = { ArrowLeft: 1, ArrowRight: -1, };

const tabRule: Object => Object = ({ theme, }) => ({
  flexGrow: '1',
  flexBasis: '0',
  position: 'relative',
  textAlign: 'center',
  ':not(:last-of-type)': {
    ':after': {
      content: '""',
      position: 'absolute',
      end: '0',
      bottom: 'calc(50% - 3px)',
      transform: 'translateY(50%)',
      width: '1px',
      height: '3rem',
      backgroundColor: theme.color('neutral', '-3'),
    },
  },
});

TabbedTable.defaultProps = {
  defaultTab: 0,
};

function TabbedTable({ panel, tabs, presentation, defaultTab, }: Props) {
  const [ controls, setControls, ] = useState(tabs[defaultTab].controls);
  const [ filters, setFilters, ] = useState(tabs[defaultTab].filters);
  const [ index, setIndex, ] = useState(defaultTab);
  const { theme, } = useFela();

  const changeSelectedTab = (client, { controls, filters, index, }) => {
    client
      .query({
        query: BondsTabsQuery,
        variables: {
          filters,
          count: 11,
          sortBy: 'name',
          sortOrder: 'asc',
          offset: 0,
        },
        fetchPolicy: 'network-only',
      })
      .then(() => {
        setControls(controls);
        setFilters(filters);
        setIndex(index);
      });
  };

  const tabRefs = tabs.map(React.createRef);
  return (
    <Tabs activeTab={index}>
      {({ setActiveTab, activeTab, }) => (
        <ApolloConsumer>
          {client => (
            <Fragment>
              <TabList
                miscStyles={{
                  color: theme.color('neutral', '-3'),
                  display: 'flex',
                  marginTop: '2rem',
                  ...theme.type(-1),
                }}
                onKeyDown={({ key, }) => {
                  const inc = incByKey[key];
                  if (!inc) return;
                  const nextActiveTab = (tabs.length + activeTab + inc) % tabs.length;
                  tabRefs[nextActiveTab].current && tabRefs[nextActiveTab].current.focus && tabRefs[nextActiveTab].current.focus();
                  setActiveTab(nextActiveTab);
                  const { controls, filters, } = tabs[nextActiveTab];
                  changeSelectedTab(client, {
                    controls,
                    filters,
                    index: nextActiveTab,
                  });
                }}
              >
                {tabs.map(({ controls, filters, display, }: TabType, i: number) => (
                  <Tab
                    index={i}
                    tabRef={tabRefs[i]}
                    isActive={activeTab === i}
                    controls={`tab-${controls}`}
                    presentation={presentation}
                    rule={tabRule}
                    onClick={() => changeSelectedTab(client, {
                      controls,
                      filters,
                      index: i,
                    })
                    }
                    setActiveTab={index => setActiveTab(index)}
                    render={TabButton}
                  >
                    <span>{display}</span>
                  </Tab>
                ))}
              </TabList>
              <TabPanel id={`tab-${controls}`}>{panel({ filters, headers: tabs[activeTab].headers, })}</TabPanel>
            </Fragment>
          )}
        </ApolloConsumer>
      )}
    </Tabs>
  );
}

export default TabbedTable;
